import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Globe, ChevronDown, Check } from "lucide-react";

export const LANGS = [
  { code: "en", name: "English" },
  { code: "hi", name: "हिन्दी (Hindi)" },
  { code: "bn", name: "বাংলা (Bengali)" },
  { code: "te", name: "తెలుగు (Telugu)" },
  { code: "mr", name: "मराठी (Marathi)" },
  { code: "ta", name: "தமிழ் (Tamil)" },
  { code: "ur", name: "اردو (Urdu)" },
  { code: "gu", name: "ગુજરાતી (Gujarati)" },
  { code: "kn", name: "ಕನ್ನಡ (Kannada)" },
  { code: "or", name: "ଓଡ଼ିଆ (Odia)" },
  { code: "ml", name: "മലയാളം (Malayalam)" },
  { code: "pa", name: "ਪੰਜਾਬੀ (Punjabi)" },
  { code: "as", name: "অসমীয়া (Assamese)" },
  { code: "mai", name: "मैथिली (Maithili)" },
  { code: "sat", name: "ᱥᱟᱱᱛᱟᱲᱤ (Santali)" },
  { code: "ks", name: "کٲشُر (Kashmiri)" },
  { code: "ne", name: "नेपाली (Nepali)" },
  { code: "sd", name: "سنڌي (Sindhi)" },
  { code: "doi", name: "डोगरी (Dogri)" },
  { code: "kok", name: "कोंकणी (Konkani)" },
  { code: "mni", name: "মৈতৈলোন্ (Manipuri)" },
  { code: "brx", name: "बड़ो (Bodo)" },
  { code: "sa", name: "संस्कृतम् (Sanskrit)" },
  { code: "bho", name: "भोजपुरी (Bhojpuri)" },
  { code: "awa", name: "अवधी (Awadhi)" },
  { code: "hne", name: "छत्तीसगढ़ी (Chhattisgarhi)" },
  { code: "mag", name: "मगही (Magahi)" },
  { code: "mwr", name: "मारवाड़ी (Marwari)" },
  { code: "tcy", name: "ತುಳು (Tulu)" },
  { code: "bgc", name: "हरियाणवी (Haryanvi)" },
];

const INCLUDED = LANGS.map((l) => l.code).join(",");

function getCookieLang() {
  const m = document.cookie.match(/googtrans=\/en\/([a-z-]+)/i);
  return m ? m[1] : "en";
}

function clearGoogTrans() {
  const host = location.hostname;
  const parts = host.split(".");
  const domains = [host, "." + host];
  for (let i = 1; i < parts.length - 1; i++) {
    const d = "." + parts.slice(i).join(".");
    if (!domains.includes(d)) domains.push(d);
  }
  domains.forEach((d) => {
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${d};`;
  });
  document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  document.cookie = "googtrans=/en/en; path=/;";
}

export function setLanguage(code) {
  document.documentElement.style.transition = "opacity 0.35s ease";
  document.documentElement.style.opacity = "0";
  setTimeout(() => {
    if (code === "en") {
      clearGoogTrans();
    } else {
      clearGoogTrans();
      document.cookie = `googtrans=/en/${code}; path=/; SameSite=Lax`;
      document.cookie = `googtrans=/en/${code}; path=/; domain=${location.hostname}; SameSite=Lax`;
    }
    localStorage.setItem("lb_lang", code);
    location.reload();
  }, 360);
}

// Injects Google Translate engine once (hidden UI, our own luxury switcher drives it)
export function TranslateInit() {
  useEffect(() => {
    if (document.getElementById("google_translate_element")) return;
    const holder = document.createElement("div");
    holder.id = "google_translate_element";
    holder.style.display = "none";
    document.body.appendChild(holder);
    window.googleTranslateElementInit = () => {
      try {
        new window.google.translate.TranslateElement(
          { pageLanguage: "en", includedLanguages: INCLUDED, autoDisplay: false },
          "google_translate_element"
        );
      } catch {}
    };
    if (!document.querySelector('script[src*="translate.google.com"]')) {
      const s = document.createElement("script");
      s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      s.async = true;
      document.body.appendChild(s);
    }
    // honor saved preference on first load
    const saved = localStorage.getItem("lb_lang");
    if (saved && saved !== "en" && getCookieLang() !== saved) {
      document.cookie = `googtrans=/en/${saved}; path=/;`;
      document.cookie = `googtrans=/en/${saved}; path=/; domain=${location.hostname}`;
      location.reload();
    }
  }, []);
  return null;
}

export default function LanguageSwitcher({ dark = false }) {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const ref = useRef(null);

  useEffect(() => { setLang(getCookieLang()); }, []);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  const panelCls = dark
    ? "bg-[#18181B] border border-[#27272A] text-[#F4F4F5]"
    : "bg-[#FBF9F5] border border-[#E2DDD5] text-[#121212]";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="language-switcher-button"
        className={`flex items-center gap-1.5 text-xs uppercase tracking-widest transition ${dark ? "text-[#C5A059] hover:text-white" : "hover:text-[#C5A059]"}`}
      >
        <Globe size={16} />
        <span className="hidden sm:inline">{current.name.split("(")[0].trim()}</span>
        <ChevronDown size={12} />
      </button>
      {open && createPortal(
        <div className={`fixed z-[9999] w-64 max-h-96 overflow-y-auto shadow-2xl ${panelCls}`}
          style={{ top: (ref.current?.getBoundingClientRect().bottom || 0) + 8, right: Math.max(12, window.innerWidth - (ref.current?.getBoundingClientRect().right || 300)) }}
          data-testid="language-panel"
          onMouseDown={(e) => e.stopPropagation()}>
          <div className="p-3">
            <p className={`text-[10px] uppercase tracking-[0.25em] mb-2 ${dark ? "text-[#8C867D]" : "text-[#8C867D]"}`}>Choose Language</p>
            {LANGS.map((l) => (
              <button key={l.code}
                data-testid={`language-option-${l.code}`}
                onClick={() => { setOpen(false); if (l.code !== lang) setLanguage(l.code); }}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between rounded transition ${dark ? "hover:bg-[#27272A]" : "hover:bg-[#EFEAE1]"} ${l.code === lang ? (dark ? "text-[#C5A059]" : "text-[#997A31] font-medium") : ""}`}>
                {l.name}
                {l.code === lang && <Check size={14} />}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
